import { supabase } from '../lib/supabase';

/**
 * Profile Service
 * Handles user profile operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

// All profile columns EXCEPT `phone` (column-level SELECT is revoked for regular
// reads). For a user's own phone, use the get_my_profile() RPC via AuthContext.
const PROFILE_COLUMNS =
  'id, email, title, first_name, last_name, country, institution, institution_type, department, academic_rank, orcid, website, subject_areas, roles, experience_level, avatar_url, bio, receive_updates, available_as_reviewer, reviewer_keywords, is_active, created_at, updated_at';

/**
 * Convert database profile (snake_case) to React format (camelCase)
 */
const convertProfileToReact = (dbProfile) => {
  if (!dbProfile) return null;

  return {
    id: dbProfile?.id,
    email: dbProfile?.email,
    title: dbProfile?.title,
    firstName: dbProfile?.first_name,
    lastName: dbProfile?.last_name,
    phone: dbProfile?.phone,
    country: dbProfile?.country,
    institution: dbProfile?.institution,
    institutionType: dbProfile?.institution_type,
    department: dbProfile?.department,
    academicRank: dbProfile?.academic_rank,
    orcid: dbProfile?.orcid,
    website: dbProfile?.website,
    subjectAreas: dbProfile?.subject_areas || [],
    roles: dbProfile?.roles || [],
    experienceLevel: dbProfile?.experience_level,
    avatarUrl: dbProfile?.avatar_url,
    availableAsReviewer: dbProfile?.available_as_reviewer ?? false,
    reviewerKeywords: dbProfile?.reviewer_keywords || [],
    bio: dbProfile?.bio,
    receiveUpdates: dbProfile?.receive_updates,
    isActive: dbProfile?.is_active,
    createdAt: dbProfile?.created_at,
    updatedAt: dbProfile?.updated_at
  };
};

/**
 * Convert React profile (camelCase) to database format (snake_case)
 */
const convertProfileToDb = (reactProfile) => {
  const dbProfile = {};

  if (reactProfile?.title !== undefined) dbProfile.title = reactProfile?.title;
  if (reactProfile?.firstName !== undefined) dbProfile.first_name = reactProfile?.firstName;
  if (reactProfile?.lastName !== undefined) dbProfile.last_name = reactProfile?.lastName;
  if (reactProfile?.phone !== undefined) dbProfile.phone = reactProfile?.phone;
  if (reactProfile?.country !== undefined) dbProfile.country = reactProfile?.country;
  if (reactProfile?.institution !== undefined) dbProfile.institution = reactProfile?.institution;
  if (reactProfile?.institutionType !== undefined) dbProfile.institution_type = reactProfile?.institutionType;
  if (reactProfile?.department !== undefined) dbProfile.department = reactProfile?.department;
  if (reactProfile?.academicRank !== undefined) dbProfile.academic_rank = reactProfile?.academicRank;
  if (reactProfile?.orcid !== undefined) dbProfile.orcid = reactProfile?.orcid;
  if (reactProfile?.website !== undefined) dbProfile.website = reactProfile?.website;
  if (reactProfile?.subjectAreas !== undefined) dbProfile.subject_areas = reactProfile?.subjectAreas;
  if (reactProfile?.roles !== undefined) dbProfile.roles = reactProfile?.roles;
  if (reactProfile?.experienceLevel !== undefined) dbProfile.experience_level = reactProfile?.experienceLevel;
  if (reactProfile?.availableAsReviewer !== undefined) dbProfile.available_as_reviewer = reactProfile?.availableAsReviewer;
  if (reactProfile?.reviewerKeywords !== undefined) dbProfile.reviewer_keywords = reactProfile?.reviewerKeywords;
  if (reactProfile?.avatarUrl !== undefined) dbProfile.avatar_url = reactProfile?.avatarUrl;
  if (reactProfile?.bio !== undefined) dbProfile.bio = reactProfile?.bio;
  if (reactProfile?.receiveUpdates !== undefined) dbProfile.receive_updates = reactProfile?.receiveUpdates;
  if (reactProfile?.isActive !== undefined) dbProfile.is_active = reactProfile?.isActive;

  return dbProfile;
};

export const profileService = {
  /**
   * Get user profile by ID
   * @param {string} userId
   * @returns {Promise<{data, error}>}
   */
  async getProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(PROFILE_COLUMNS)?.eq('id', userId)?.single();

      if (error) throw error;
      return { data: convertProfileToReact(data), error: null };
    } catch (error) {
      console.error('Get profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user profile
   * @param {string} userId
   * @param {Object} updates - Profile updates in camelCase
   * @returns {Promise<{data, error}>}
   */
  async updateProfile(userId, updates) {
    try {
      const dbUpdates = convertProfileToDb(updates);

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select(PROFILE_COLUMNS)?.single();

      if (error) throw error;
      return { data: convertProfileToReact(data), error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Search the reviewer directory (users who opted in to being findable).
   * Matches name, institution and expertise keywords via the search_reviewers
   * RPC. Returns lightweight reviewer records.
   * @param {string} query
   * @returns {Promise<Array>}
   */
  async searchReviewers(query = '') {
    try {
      const { data, error } = await supabase?.rpc('search_reviewers', { search_query: query?.trim() });
      if (error) throw error;
      return (data || [])?.map(p => ({
        id: p?.id,
        firstName: p?.first_name,
        lastName: p?.last_name,
        email: p?.email,
        institution: p?.institution,
        avatarUrl: p?.avatar_url,
        keywords: p?.reviewer_keywords || []
      }));
    } catch (error) {
      console.error('Search reviewers error:', error);
      throw error;
    }
  },

  /**
   * Get all profiles (for admin/organizer use)
   * @param {Object} filters - Optional filters
   * @returns {Promise<{data, error}>}
   */
  async getAllProfiles(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select(PROFILE_COLUMNS);

      if (filters?.roles && filters?.roles?.length > 0) {
        query = query?.contains('roles', filters?.roles);
      }

      if (filters?.subjectAreas && filters?.subjectAreas?.length > 0) {
        query = query?.contains('subject_areas', filters?.subjectAreas);
      }

      if (filters?.institution) {
        query = query?.ilike('institution', `%${filters?.institution}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data?.map(convertProfileToReact), error: null };
    } catch (error) {
      console.error('Get all profiles error:', error);
      return { data: null, error };
    }
  },

  /**
   * Search profiles by name or email
   * @param {string} searchTerm
   * @returns {Promise<{data, error}>}
   */
  async searchProfiles(searchTerm) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(PROFILE_COLUMNS)?.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);

      if (error) throw error;
      return { data: data?.map(convertProfileToReact), error: null };
    } catch (error) {
      console.error('Search profiles error:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete user profile
   * @param {string} userId
   * @returns {Promise<{error}>}
   */
  async deleteProfile(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete profile error:', error);
      return { error };
    }
  },

  async uploadAvatar(file, userId) {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;
    const { data, error } = await supabase?.storage?.from('conference-submissions')?.upload(filePath, file, { cacheControl: '3600', upsert: true });
    if (error) throw error;
    return data?.path;
  },

  async getAvatarUrl(filePath) {
    if (!filePath) return null;
    const { data, error } = await supabase?.storage?.from('conference-submissions')?.createSignedUrl(filePath, 3600);
    if (error) throw error;
    return data?.signedUrl;
  },

  async deleteAvatar(filePath) {
    if (!filePath) return;
    const { error } = await supabase?.storage?.from('conference-submissions')?.remove([filePath]);
    if (error) throw error;
  }
};